/**
 * @copyright © 2026 ASAP Technology. All rights reserved.
 * @license BSL-1.1 (see LICENSE file)
 *
 * @file embed.ts
 * @description Modelo embebido (Modelo A): comunicación iframe → parent vía
 * postMessage. El formulario completo se embebe en un iframe del comercio; en
 * lugar de redirigir el browser (modelo redirect), notifica el resultado al
 * parent por postMessage. La confirmación real sigue siendo server-to-server
 * (el comercio ejecuta OrderFinal). Nunca viajan datos de tarjeta por postMessage.
 */

export const EMBED_API_VERSION = '1.0';

/** Tipos de mensaje iframe → parent. */
export const EMBED_MSG = {
  READY: 'paymentform:ready',         // el form cargó y está listo
  RESIZE: 'paymentform:resize',       // alto del contenido cambió (responsive)
  PAYMENT_COMPLETED: 'paymentform:payment-completed', // terminó un intento/tramo
  FLOW_COMPLETED: 'paymentform:flow-completed',       // el comercio ya puede ejecutar OrderFinal
  SUBMITTED: 'paymentform:submitted', // compatibilidad 1.0: resultado agregado
  ERROR: 'paymentform:error',         // error de proceso
} as const;

/** ¿El form está corriendo dentro de un iframe (modelo embebido)? */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access a window.top lanza → estamos embebidos cross-origin.
    return true;
  }
}

/**
 * Resuelve el targetOrigin para postMessage:
 *  - si el origin del referrer (parent) está en la whitelist → ése (lo más seguro);
 *  - si hay whitelist pero el referrer no matchea → el primero de la whitelist;
 *  - si no hay whitelist configurada → el origin exacto del referrer;
 *  - sin un origin exacto comprobable → cadena vacía y no se envía el mensaje.
 */
export function resolveTargetOrigin(allowed: string[] = [], referrer: string = (typeof document !== 'undefined' ? document.referrer : '')): string {
  let refOrigin = '';
  try { refOrigin = referrer ? new URL(referrer).origin : ''; } catch { refOrigin = ''; }
  if (allowed.length === 0) return refOrigin;
  if (refOrigin && allowed.includes(refOrigin)) return refOrigin;
  return allowed[0];
}

/** Envía un mensaje al parent (no-op si no estamos embebidos). */
export function postToParent(type: string, payload: Record<string, unknown>, targetOrigin: string): void {
  if (!isEmbedded() || !targetOrigin || targetOrigin === '*') return;
  try {
    window.parent.postMessage({ source: 'paymentform', apiVersion: EMBED_API_VERSION, type, ...payload }, targetOrigin);
  } catch {
    /* parent no disponible — ignorar */
  }
}
